import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ADS_DUPLICATE_NAME_MAX, ADS_TEMPLATE_DESCRIPTION_MAX, ADS_TEMPLATE_NAME_MAX, AdsNamingInput, AdsNamingSampleRequest, AdsTemplateKind, } from '@wit-support/admin-contracts';
import { Badge, ConfirmDialog, ErrorCard, InlineNotice, SectionCard, Skeleton, toast, useDebouncedValue, } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm, useZodFormContext } from '@wit-support/ui-forms';
import { useBreadcrumbDetail } from '@wit-support/ui-domain';
import { CircleAlert, Lightbulb, Lock, TriangleAlert } from 'lucide-react';
import { adsIssuesOf, createTemplate, getNamingSample, getTemplate, updateTemplate, } from '../api.js';
import { ADS_QUERY_DEFAULTS } from '../query-policy.js';
import { useAdsLauncherPerms } from '../perms.js';
import { ADS_KEY, AdsScreen, NAMING_SAMPLE_KEY, describeAdsError, submitting, templateKey, useAccounts, useAdsHealth, useCountryLists, useLocales, useRegistry, } from '../shared.js';
import { RegistryDataProvider } from '../registry/field-widget.js';
import { dependencyChange, withDependentDefaults } from '../registry/dependent-defaults.js';
import { FixedFieldsList, RegistryFields } from '../registry/registry-form.js';
import { GENDERS_KEY, GenderAdvantageNotice } from '../components/gender-advantage-notice.js';
import { LADDER_KEYS, LadderFields, ladderKeyActive } from '../components/launch-structure.js';
import { buildRegistrySchema, fieldKeyOf, fieldName, fromFormValues, toFormValues, toUiValue, validateValue, } from '../registry/to-zod.js';
import { GROUP_LABEL_RU, TEMPLATE_HINT_NOISE, fixedDescs, hintWithout, isTemplateEditorDesc, joinHint, slotNamesOf, sortDescs, } from '../registry/use-registry.js';
import { TEMPLATE_KIND_LABEL_RU } from '../components/launch-template-cards.js';
import { CommonFieldGroupsNote, TemplateGroups, groupContractValues, groupsToForm, groupsToInput, } from '../components/template-groups.js';
import { GROUP_BID_KEY, groupLabelError, hasValue, isGroupIssue, groupRules, groupsFeature, } from '../groups.js';
import { splitTemplateIssues } from '../issues.js';
import { FunnelCatalogLoading, TemplateFunnelSection, funnelIssueFieldPath, hasTemplateFunnel, isFunnelIssue, templateFunnelDefaults, templateFunnelInput, templateFunnelModel, templateFunnelSchema, useFunnelSettingsCatalog, } from '../funnel-settings.js';
let pendingDraft = null;
export function setPendingTemplateDraft(d) {
    pendingDraft = d;
}
/**
 * Имена нового шаблона: шаблоны имён — серверные (`/registry` →
 * `namingDefaults`, v4 D11a), своей копии в UI нет; префикса по умолчанию нет
 * (v4 D7) — его вводит человек. Старый auto-ads без `namingDefaults` — поля
 * пустые и обязательные.
 */
export function defaultNaming(registry) {
    return {
        prefix: '',
        campaign: registry.namingDefaults?.campaign ?? '',
        adset: registry.namingDefaults?.adset ?? '',
        ad: registry.namingDefaults?.ad ?? '',
    };
}
const le = (key) => `le__${fieldName(key)}`;
/**
 * Опции карточки «Структура» (v4 §11.1, только при `features.builderV4`):
 * раскладка и копии (по умолчанию при запуске), шаг бюджета копии, уникальные
 * центы и лесенка ставок. Переезжают без потери из «Копии и правила» и
 * «Значения по умолчанию при запуске».
 */
export const STRUCTURE_OPTION_KEYS = new Set([
    'opt.layout',
    'opt.copies',
    'opt.budgetStepPct',
    'opt.uniqueCents',
    'opt.bidStepMinor',
    'opt.bidStepDirection',
    'opt.bidFloorMinor',
    'opt.bidCeilingMinor',
]);
/**
 * Опции шаблона про создание и старт — своим блоком «Создание и старт»;
 * остальные опции — «Копии и правила».
 */
const START_OPTION_KEYS = new Set([
    'opt.createMode',
    'opt.immediateLeadMinutes',
    'opt.minLeadMinutes',
    'opt.maxLatenessMinutes',
    'opt.staggerMinutes',
    'opt.accountSpacingSec',
    'opt.mondayGuard',
]);
/**
 * Поле в редакторе шаблона, которое можно оставить пустым:
 * - поле запуска — «значение по умолчанию при запуске» (пусто — умолчание
 *   реестра);
 * - поле «обязательно при запуске, если пусто» (ставка, ROAS) — пустое
 *   спросит экран запуска.
 * `required` снят, пределы остаются.
 */
function asTemplateDefault(d) {
    if (d.mode === 'template' && d.requiredAtLaunchWhenEmpty) {
        return {
            ...d,
            hint: joinHint(hintWithout(d.hint, TEMPLATE_HINT_NOISE), 'Пусто — спросят при каждом запуске'),
            ...(d.constraints ? { constraints: { ...d.constraints, required: false } } : {}),
        };
    }
    if (d.mode !== 'launch' || !d.constraints?.required)
        return d;
    return { ...d, constraints: { ...d.constraints, required: false } };
}
/**
 * Контекст видимости формы шаблона: значения шаблона/умолчания под ТЕКУЩИМИ
 * значениями обеих групп формы (поля ↔ опции). Один и тот же — для показа
 * полей, проверки схемы и отбрасывания спрятанных ключей при сохранении.
 */
function editorContext(base, fieldDescs, optionDescs, form) {
    return {
        ...base,
        ...fromFormValues(fieldDescs, form.values),
        ...fromFormValues(optionDescs, form.options),
    };
}
export function TemplatePage() {
    const { id } = useParams({ strict: false });
    return (_jsx(AdsScreen, { title: id ? 'Шаблон' : 'Новый шаблон', width: "narrow", children: _jsx(TemplateBody, { id: id ?? null }) }));
}
/**
 * Редактор шаблона без оболочки страницы — для правки одноразовой копии кампании прямо на
 * экране запуска (`routes/duplicate-launch.tsx`): без карточки «Шаблон» (название, тип, кабинеты
 * у копии не правятся), без липкой панели, без крошки. Те же поля, те же проверки и тот же PUT.
 */
export function EmbeddedTemplateEditor({ id, onDirtyChange, }) {
    return _jsx(TemplateBody, { id: id, embedded: true, onDirtyChange: onDirtyChange });
}
/** Сообщает наружу, есть ли в форме несохранённые правки; при закрытии формы — что их нет. */
function DirtyBridge({ onChange }) {
    const { form } = useZodFormContext();
    const dirty = form.formState.isDirty;
    useEffect(() => {
        onChange(dirty);
    }, [dirty, onChange]);
    useEffect(() => () => onChange(false), [onChange]);
    return null;
}
function TemplateBody({ id, embedded = false, onDirtyChange, }) {
    const registry = useRegistry();
    // раскладка карточек зависит от `features.builderV4` — ждём /health, чтобы не прыгала (ревью SB-4 P2-8)
    const health = useAdsHealth();
    const tpl = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: templateKey(id ?? 'new'),
        queryFn: () => getTemplate(id),
        enabled: id != null,
    });
    const [draft] = useState(() => {
        const d = id == null ? pendingDraft : null;
        pendingDraft = null;
        return d;
    });
    // Каталог настроек воронки: его сбой не мешает редактору — секция
    // показывает понятный текст и «Повторить», шаблон сохраняется без изменения
    // настроек. Ждём каталог, только если у шаблона настройки есть; иначе форма
    // открывается сразу и пересобирается, когда каталог придёт (её не трогали).
    const catalog = useFunnelSettingsCatalog();
    // Снимок формы для перестройки под пришедший каталог — РАЗОВЫЙ и привязан к
    // версии шаблона и поколению редактора, для которых снят: применяется при
    // монтировании этого поколения и сразу сбрасывается; новая версия шаблона
    // (после сохранения) его не получает — иначе старые значения легли бы
    // поверх сохранённых и ушли бы при следующем сохранении.
    const [rebuild, setRebuild] = useState({
        gen: 0,
        restore: null,
    });
    const tplKey = tpl.data ? `${tpl.data.id}@${tpl.data.version}` : 'new';
    const tplKeyRef = useRef(tplKey);
    tplKeyRef.current = tplKey;
    const { refetch } = catalog;
    const control = useMemo(() => ({
        loading: catalog.isPending,
        latest: catalog.data ?? null,
        retrying: catalog.isFetching,
        retry: () => void refetch(),
        rebuild: (restore) => setRebuild((r) => ({
            gen: r.gen + 1,
            restore: restore ? { values: restore, forKey: tplKeyRef.current, gen: r.gen + 1 } : null,
        })),
    }), [catalog.isPending, catalog.data, catalog.isFetching, refetch]);
    const restore = rebuild.restore && rebuild.restore.forKey === tplKey && rebuild.restore.gen === rebuild.gen
        ? rebuild.restore.values
        : null;
    // Сменилась версия шаблона — чужой снимок больше не нужен (не применяется и не хранится).
    useEffect(() => {
        if (rebuild.restore && rebuild.restore.forKey !== tplKey) {
            setRebuild((r) => ({ ...r, restore: null }));
        }
    }, [rebuild.restore, tplKey]);
    const consumeRestore = useMemo(() => () => setRebuild((r) => (r.restore ? { ...r, restore: null } : r)), []);
    useBreadcrumbDetail(embedded ? null : (tpl.data?.name ?? null));
    const err = registry.error ?? tpl.error;
    if (err)
        return _jsx(ErrorCard, { children: describeAdsError(err) });
    const waitCatalog = tpl.data != null && hasTemplateFunnel(tpl.data.funnelSettings);
    if (!registry.data || (id != null && !tpl.data) || health.isPending) {
        return _jsx(Skeleton, { className: "h-96 w-full" });
    }
    if (waitCatalog && catalog.isPending)
        return _jsx(FunnelCatalogLoading, { height: "h-96" });
    return (_jsx(TemplateEditor, { registry: registry.data, template: tpl.data ?? null, draft: draft, catalog: catalog.data ?? null, catalogError: catalog.error, control: control, restore: restore, onRestoreApplied: consumeRestore, embedded: embedded, onDirtyChange: onDirtyChange }, `${tpl.data ? `${tpl.data.id}@${tpl.data.version}` : 'new'}#${rebuild.gen}`));
}
/**
 * `naming` для PUT: имя копии (`campaignOverride`) уходит обратно, пока оно задано, — иначе правка
 * любого поля шаблона-копии молча стёрла бы имя. Очищенное поле — имя убирается; пробелы по краям
 * срезаются.
 */
export function namingOf(naming) {
    const { campaignOverride, ...rest } = naming;
    const name = campaignOverride?.trim() ?? '';
    return name === '' ? rest : { ...rest, campaignOverride: name };
}
function TemplateEditor({ registry, template, draft, catalog, catalogError, control, restore: restoreProp, onRestoreApplied, embedded = false, onDirtyChange, }) {
    // Снимок берётся один раз — при монтировании этого поколения редактора.
    const [restore] = useState(restoreProp);
    useEffect(() => {
        if (restore)
            onRestoreApplied();
        // только при монтировании
    }, []);
    // Снимок каталога при открытии формы: фоновое обновление каталога не
    // перестраивает схему уже открытой формы (новая версия шаблона — новый снимок).
    const [funnel] = useState(() => templateFunnelModel(catalog, catalogError, template?.funnelSettings ?? null));
    const { canManageTemplates } = useAdsLauncherPerms();
    // v4 (карточка «Структура», «Правила и сторожа») — только при новом auto-ads
    const health = useAdsHealth();
    const v4 = health.data?.features?.builderV4 === true;
    // группы шаблона (v4 §11.1): видны при `features.adSetGroups`; сбой /health — «Повторить»
    const groupsOn = groupsFeature(health.data, health.isError, health.isPending);
    const groupable = useMemo(() => registry.fields.filter((d) => d.groupable && d.enabled), [registry]);
    const rules = groupRules(registry);
    const savedGroups = template?.adSetGroups?.groups ?? draft?.adSetGroups ?? [];
    const hadGroups = (template?.adSetGroups?.groups.length ?? 0) > 0;
    const [movedLookalike, setMovedLookalike] = useState(null);
    const qc = useQueryClient();
    const navigate = useNavigate();
    const accounts = useAccounts();
    const locales = useLocales();
    const countryLists = useCountryLists();
    const [issues, setIssues] = useState([]);
    // Замечания сохранения по настройкам воронки, которым нет места у полей формы
    const [funnelIssues, setFunnelIssues] = useState([]);
    const [conflict, setConflict] = useState(false);
    // Поля и опции шаблона + поля запуска (бюджет, старт, копии, …) как
    // «значение по умолчанию при запуске»: раньше редактор их не показывал и
    // при сохранении молча стирал из `values`.
    const fieldDescs = useMemo(() => sortDescs(registry.fields.filter(isTemplateEditorDesc).map(asTemplateDefault)), [registry]);
    // У копии кампании включение зафиксировано «вручную» (auto-ads: другое значение — 400) — не предлагаем
    const fixedActivation = template?.origin?.kind === 'duplicate';
    const optionDescs = useMemo(() => sortDescs(registry.options
        .filter(isTemplateEditorDesc)
        .filter((d) => !(fixedActivation && d.key === 'opt.activation'))
        .map(asTemplateDefault)), [registry, fixedActivation]);
    const tplFields = useMemo(() => fieldDescs.filter((d) => d.mode === 'template'), [fieldDescs]);
    const launchFields = useMemo(() => fieldDescs.filter((d) => d.mode === 'launch'), [fieldDescs]);
    const inStructure = (d) => v4 && STRUCTURE_OPTION_KEYS.has(d.key);
    const tplOptions = useMemo(() => optionDescs.filter((d) => d.mode === 'template' && !START_OPTION_KEYS.has(d.key) && !inStructure(d)), [optionDescs, v4]);
    const structureOptions = useMemo(() => (v4 ? optionDescs.filter((d) => STRUCTURE_OPTION_KEYS.has(d.key)) : []), [optionDescs, v4]);
    const startOptions = useMemo(() => optionDescs.filter((d) => d.mode === 'template' && START_OPTION_KEYS.has(d.key)), [optionDescs]);
    const launchOptions = useMemo(() => optionDescs.filter((d) => d.mode === 'launch' && !inStructure(d)), [optionDescs, v4]);
    // Флажок «можно менять при запуске» — только у ПОЛЕЙ реестра: auto-ads
    // ищет ключи `launchEditable` и `overrides` пачки только среди полей
    // (`fieldByKey`), опция `opt.*` там — 400 `not-launch-editable` /
    // `unknown-field`.
    const flagged = fieldDescs;
    const fixed = useMemo(() => fixedDescs(registry), [registry]);
    const withDefaults = (descs, src) => {
        const out = {};
        for (const d of descs) {
            if (src && d.key in src)
                out[d.key] = src[d.key];
            else if (d.default !== null && d.default !== undefined)
                out[d.key] = d.default;
        }
        return out;
    };
    const srcValues = template?.values ?? draft?.values ?? {};
    // зависимые умолчания (`defaultWhen`) — только для ключей, которых нет в шаблоне
    const values = withDependentDefaults(fieldDescs, withDefaults(fieldDescs, srcValues), srcValues);
    const options = withDefaults(optionDescs, template?.options ?? draft?.options ?? undefined);
    // Ключи, которых редактор не показывает (выключенные поля реестра, поля,
    // которых реестр уже не знает, …), сохранение не должно молча стереть.
    const keep = (src, edited) => {
        const keys = new Set(edited.map((d) => d.key));
        return Object.fromEntries(Object.entries(src).filter(([k]) => !keys.has(k)));
    };
    const keptValues = keep(srcValues, fieldDescs);
    const keptOptions = keep(template?.options ?? {}, optionDescs);
    const context = { ...values, ...options };
    // Видимость полей одной группы зависит от другой (опции ↔ поля): схема
    // читает живой контекст формы через ref (его обновляет `EditorBody`), а не
    // пересобирается каждый рендер.
    const contextRef = useRef(context);
    const schema = useMemo(() => {
        const flags = {};
        for (const d of flagged) {
            // Поле запуска меняется при запуске всегда — флажок только у полей шаблона.
            if (d.mode === 'template') {
                flags[le(d.key)] = z.boolean().meta({ label: 'можно менять при запуске' });
            }
        }
        return z.object({
            name: z
                .string()
                .trim()
                .min(1, { message: 'Название обязательно' })
                .max(ADS_TEMPLATE_NAME_MAX)
                .meta({ label: 'Название', required: true }),
            description: z
                .string()
                .max(ADS_TEMPLATE_DESCRIPTION_MAX)
                .optional()
                .meta({ label: 'Описание', widget: 'textarea' }),
            kind: AdsTemplateKind.meta({
                label: 'Тип',
                options: AdsTemplateKind.options.map((k) => ({
                    value: k,
                    label: TEMPLATE_KIND_LABEL_RU[k],
                })),
            }),
            platformAccountIds: z.array(z.string()).meta({
                label: 'Кабинеты',
                description: 'Пусто — шаблон доступен в любом кабинете.',
                widget: 'multiselect',
                placeholder: 'Найти кабинет…',
            }),
            naming: z.object({
                prefix: z
                    .string()
                    .min(1, { message: 'Заполните префикс имён' })
                    .max(20, { message: 'Префикс — не длиннее 20 знаков' })
                    .regex(/^[A-Za-z0-9_-]+$/, { message: 'Префикс — латиница, цифры, «_» и «-»' })
                    .meta({ label: 'Префикс', required: true, placeholder: 'Например, ST' }),
                campaign: AdsNamingInput.shape.campaign.meta({ label: 'Кампания', required: true }),
                adset: AdsNamingInput.shape.adset.meta({ label: 'Группа объявлений', required: true }),
                ad: AdsNamingInput.shape.ad.meta({ label: 'Объявление', required: true }),
                // Имя копии кампании живёт в `naming`: без поля в схеме PUT потерял бы его
                campaignOverride: z
                    .string()
                    .max(ADS_DUPLICATE_NAME_MAX, { message: `Не длиннее ${ADS_DUPLICATE_NAME_MAX} знаков` })
                    .refine((v) => !/[{}]/.test(v), { message: 'Нельзя использовать { и }' })
                    .optional()
                    .meta({
                    label: 'Имя копии',
                    placeholder: 'Например, GOOSE LAL copy',
                    description: 'Латиницей: кириллицу система запишет транслитом, а в конец дозапишет свой маркер запуска. Пусто — стандартное имя конструктора.',
                }),
            }),
            values: buildRegistrySchema(fieldDescs, { context: () => contextRef.current }),
            // в «Структуре» (v4) неактивная граница лесенки скрыта — и не проверяется (Codex #340)
            options: buildRegistrySchema(optionDescs, {
                context: () => contextRef.current,
                ...(v4
                    ? {
                        isActive: (d, all) => ladderKeyActive(d.key, all),
                    }
                    : {}),
            }),
            flags: z.object(flags),
            funnel: templateFunnelSchema(funnel),
            groups: templateGroupsSchema(rules, groupsOn === 'on'),
        });
    }, [flagged, fieldDescs, optionDescs, funnel, v4, groupsOn, rules.max, rules.templateMin]);
    const computedDefaults = {
        name: template?.name ?? '',
        description: template?.description ?? '',
        kind: template?.kind ?? 'test',
        platformAccountIds: template?.platformAccountIds ?? [],
        naming: {
            ...defaultNaming(registry),
            ...Object.fromEntries(Object.entries(draft?.naming ?? {}).filter(([, v]) => typeof v === 'string' && v !== '')),
            ...(template?.naming ?? {}),
        },
        values: toFormValues(fieldDescs, values),
        options: toFormValues(optionDescs, options),
        flags: Object.fromEntries(flagged
            .filter((d) => d.mode === 'template')
            .map((d) => [
            le(d.key),
            template ? template.launchEditable.includes(d.key) : d.launchEditableDefault,
        ])),
        funnel: templateFunnelDefaults(funnel),
        groups: groupsToForm(savedGroups, groupable),
    };
    // Перестройка под пришедший каталог: всё, что человек уже ввёл, — как было,
    // настройки воронки — заново по каталогу.
    const defaultValues = restore
        ? { ...computedDefaults, ...restore, funnel: templateFunnelDefaults(funnel) }
        : computedDefaults;
    const save = useMutation({
        mutationFn: (input) => template
            ? updateTemplate(template.id, { ...input, expectedVersion: template.version })
            : createTemplate(input),
        onSuccess: (t) => {
            toast.success(template ? `Шаблон сохранён, версия ${t.version}` : 'Шаблон создан');
            setIssues([]);
            setFunnelIssues([]);
            void qc.invalidateQueries({ queryKey: ADS_KEY });
            if (!template)
                void navigate({ to: '/fb-launches/templates/$id', params: { id: t.id } });
        },
    });
    const accountOptions = (accounts.data?.items ?? []).map((a) => ({
        value: a.platformAccountId,
        label: a.name,
    }));
    return (_jsxs(RegistryDataProvider, { value: {
            registry,
            locales: locales.data ?? null,
            countryLists: countryLists.data?.items ?? null,
            platformAccountId: accounts.data?.items[0]?.platformAccountId ?? null,
            audienceSlotNames: slotNamesOf(accounts.data?.items ?? []),
        }, children: [_jsxs("div", { className: "flex flex-col gap-4", children: [template ? (_jsxs("div", { className: "flex flex-wrap items-center gap-2 text-sm", children: [_jsxs(Badge, { variant: "outline", children: ["\u0432\u0435\u0440\u0441\u0438\u044F ", template.version] }), template.archived ? _jsx(Badge, { variant: "warning", children: "\u0432 \u0430\u0440\u0445\u0438\u0432\u0435" }) : null, _jsxs("span", { className: "text-[var(--color-text-muted)]", children: ["\u0438\u0437\u043C\u0435\u043D\u0438\u043B ", template.updatedBy ?? template.createdBy ?? '—'] })] })) : null, template ? _jsx(TemplateIssuesNotice, { issues: template.issues }) : null, draft ? (_jsx(InlineNotice, { variant: "info", bordered: true, as: "div", "data-testid": "draft-notice", children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsxs("span", { children: ["\u0427\u0435\u0440\u043D\u043E\u0432\u0438\u043A \u0438\u0437 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 ", draft.sourceLabel, ". \u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u0438 \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u0435."] }), draft.warnings.length > 0 ? (_jsx("ul", { className: "flex list-disc flex-col gap-0.5 pl-4", children: draft.warnings.map((w, i) => (_jsx("li", { children: w.message }, `${w.code}-${i}`))) })) : null] }) })) : null, !canManageTemplates ? (_jsx(InlineNotice, { variant: "muted", as: "div", children: "\u041C\u0435\u043D\u044F\u0442\u044C \u0448\u0430\u0431\u043B\u043E\u043D\u044B \u043C\u043E\u0433\u0443\u0442 \u0441\u0443\u043F\u0435\u0440\u0432\u0430\u0439\u0437\u0435\u0440 \u0438 \u0432\u043B\u0430\u0434\u0435\u043B\u0435\u0446 \u2014 \u0437\u0434\u0435\u0441\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440." })) : null, _jsxs(ZodForm, { schema: schema, defaultValues: defaultValues, readonly: !canManageTemplates, fieldMetaOverrides: { platformAccountIds: { options: accountOptions } }, className: "flex flex-col gap-4", onSubmit: (raw, form) => {
                            const v = raw;
                            const flags = v.flags;
                            // Тот же контекст, по которому экран прятал поля: спрятанное
                            // `visibleWhen` поле (ставка при стратегии без капа) не сохраняется.
                            const ctx = editorContext(context, fieldDescs, optionDescs, v);
                            // неактивная опция лесенки (скрыта в «Структуре») сохраняется, если её
                            // значение годно; негодное не отправляется — сервер его не примет, а
                            // форма о нём не говорит (Codex #340)
                            const activeOrValid = (opts) => v4
                                ? Object.fromEntries(Object.entries(opts).filter(([k, val]) => {
                                    const d = optionDescs.find((x) => x.key === k);
                                    return !d || ladderKeyActive(k, ctx) || validateValue(d, val) === null;
                                }))
                                : opts;
                            const input = {
                                name: v.name,
                                ...(v.description ? { description: v.description } : {}),
                                kind: v.kind,
                                values: {
                                    ...keptValues,
                                    ...fromFormValues(fieldDescs, v.values, {
                                        context: ctx,
                                    }),
                                },
                                options: {
                                    ...keptOptions,
                                    ...activeOrValid(fromFormValues(optionDescs, v.options, {
                                        context: ctx,
                                    })),
                                },
                                launchEditable: flagged
                                    .filter((d) => d.mode === 'template' && flags[le(d.key)])
                                    .map((d) => d.key),
                                naming: namingOf(v.naming),
                                platformAccountIds: v.platformAccountIds,
                            };
                            const funnelSettings = templateFunnelInput(funnel, v.funnel);
                            if (funnelSettings)
                                input.funnelSettings = funnelSettings;
                            // группы: при выключенном/непроверенном флаге — только «убрать» (null), иначе не трогаем
                            const groupsPart = groupsToInput(v.groups, groupable, hadGroups);
                            if (groupsOn === 'on' || groupsPart.adSetGroups === null) {
                                Object.assign(input, groupsPart);
                            }
                            return submitting(save.mutateAsync(input).catch((err) => {
                                if (err instanceof ModuleRequestError &&
                                    (err.upstreamCode === 'template-version-conflict' ||
                                        adsIssuesOf(err).some((i) => i.code === 'group-key-unknown'))) {
                                    setConflict(true);
                                }
                                else if (err instanceof ModuleRequestError &&
                                    err.upstreamCode === 'template-name-taken') {
                                    form.setError('name', {
                                        type: 'server',
                                        message: 'Шаблон с таким названием уже есть',
                                    });
                                }
                                else {
                                    const issues = adsIssuesOf(err);
                                    if (issues.length > 0)
                                        setIssues(issues);
                                    // замечания настроек воронки — под их поля; без места в форме — списком в секции
                                    const unplaced = [];
                                    for (const i of issues.filter(isFunnelIssue)) {
                                        const path = funnelIssueFieldPath(funnel, i);
                                        if (path)
                                            form.setError(path, { type: 'server', message: i.message });
                                        else
                                            unplaced.push(i);
                                    }
                                    setFunnelIssues(unplaced);
                                    toast.error(describeAdsError(err, 'write'));
                                }
                                throw err;
                            }));
                        }, children: [onDirtyChange ? _jsx(DirtyBridge, { onChange: onDirtyChange }) : null, embedded && template?.origin?.kind === 'duplicate' ? (_jsx(SectionCard, { title: "\u0418\u043C\u044F \u043A\u043E\u043F\u0438\u0438", children: _jsx(ZodField, { name: "naming.campaignOverride" }) })) : null, embedded ? null : (_jsx(SectionCard, { title: "\u0428\u0430\u0431\u043B\u043E\u043D", children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(ZodField, { name: "name" }), _jsx(ZodField, { name: "description" }), _jsx(ZodField, { name: "kind" }), _jsx(ZodField, { name: "platformAccountIds" })] }) })), _jsx(EditorBody, { base: context, fieldDescs: fieldDescs, optionDescs: optionDescs, tplFields: tplFields, tplOptions: tplOptions, structureOptions: structureOptions, v4: v4, startOptions: startOptions, launchFields: launchFields, launchOptions: launchOptions, issues: issues, contextRef: contextRef, saved: srcValues, groups: {
                                    groupable,
                                    feature: groupsOn,
                                    onRetryFeature: () => void health.refetch(),
                                    canEdit: canManageTemplates,
                                    savedHasGroups: hadGroups,
                                    max: rules.max,
                                    templateMin: rules.templateMin,
                                    movedLookalike,
                                    onLookalikeMoved: setMovedLookalike,
                                    savedIssues: template?.issues ?? [],
                                    labelOf: (k) => [...registry.fields, ...registry.options].find((d) => d.key === k)?.label ??
                                        'поле, которого сейчас нет в реестре',
                                } }), _jsx("div", { id: "template-naming", className: "scroll-mt-4", children: _jsx(SectionCard, { title: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u044F", children: _jsx(NamingFields, { tokens: registry.namingTokens }) }) }), _jsx(TemplateFunnelSection, { model: funnel, control: control, issues: funnelIssues }), _jsx(SectionCard, { title: "\u0417\u0430\u0444\u0438\u043A\u0441\u0438\u0440\u043E\u0432\u0430\u043D\u043E \u0441\u0438\u0441\u0442\u0435\u043C\u043E\u0439", hint: _jsxs("span", { className: "inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)]", children: [_jsx(Lock, { "aria-hidden": true, className: "h-3 w-3" }), "\u042D\u0442\u0438 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u0437\u0430\u0434\u0430\u0451\u0442 \u043A\u043E\u043D\u0441\u0442\u0440\u0443\u043A\u0442\u043E\u0440 \u2014 \u0438\u0445 \u043D\u0435\u043B\u044C\u0437\u044F \u043F\u043E\u043C\u0435\u043D\u044F\u0442\u044C \u043D\u0438 \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435, \u043D\u0438 \u043F\u0440\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u0435."] }), children: _jsx(FixedFieldsList, { descriptors: fixed }) }), canManageTemplates ? (_jsx(Actions, { className: embedded ? 'py-3' : 'sticky bottom-0 z-10 bg-[var(--color-surface-canvas)] py-3', children: _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: embedded ? 'Сохранить правки копии' : template ? 'Сохранить' : 'Создать шаблон' }) })) : null] })] }), _jsx(ConfirmDialog, { open: conflict, onOpenChange: setConflict, title: "\u0428\u0430\u0431\u043B\u043E\u043D \u0438\u0437\u043C\u0435\u043D\u0438\u043B\u0438", description: "\u041F\u043E\u043A\u0430 \u0432\u044B \u0440\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u043B\u0438, \u0448\u0430\u0431\u043B\u043E\u043D \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u043B \u043A\u0442\u043E-\u0442\u043E \u0434\u0440\u0443\u0433\u043E\u0439. \u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043D\u043E\u0432\u0443\u044E \u0432\u0435\u0440\u0441\u0438\u044E? \u0412\u0430\u0448\u0438 \u043F\u0440\u0430\u0432\u043A\u0438 \u043D\u0430 \u044D\u0442\u043E\u043C \u044D\u043A\u0440\u0430\u043D\u0435 \u043F\u0440\u043E\u043F\u0430\u0434\u0443\u0442.", cancelLabel: "\u041E\u0441\u0442\u0430\u0442\u044C\u0441\u044F", actionLabel: "\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043D\u043E\u0432\u0443\u044E \u0432\u0435\u0440\u0441\u0438\u044E", actionStyle: "primary", onConfirm: () => {
                    if (template)
                        void qc.invalidateQueries({ queryKey: templateKey(template.id) });
                } })] }));
}
/**
 * Поля реестра редактора внутри формы: группы шаблона, «Копии и правила» и
 * «Значения по умолчанию при запуске». Видимость — по живому контексту обеих
 * групп формы; он же уходит в схему (`contextRef`).
 */
function EditorBody(props) {
    const { form } = useZodFormContext();
    const formGroups = form.watch('groups') ?? [];
    const kind = form.watch('kind') ?? 'test';
    const live = editorContext(props.base, props.fieldDescs, props.optionDescs, {
        values: form.watch('values'),
        options: form.watch('options'),
    });
    props.contextRef.current = live;
    // «можно менять при запуске»: флажок общей ставки или пустая общая ставка (её спросит запуск, P2-7)
    const bidLaunchEditable = form.watch(`flags.${le(GROUP_BID_KEY)}`) === true || live[GROUP_BID_KEY] == null;
    // замечания групп — только на их карточках, общим полям — без них (ревью SB-7 P1-1)
    const commonIssues = props.issues.filter((i) => !isGroupIssue(i));
    // общее поле, которое задаёт КАЖДАЯ группа, неактивно (§11.1)
    const everyGroupKeys = new Set(formGroups.length === 0
        ? []
        : props.groups.groupable
            .filter((d) => formGroups.every((g) => hasValue(groupContractValues(g, props.groups.groupable)[d.key])))
            .map((d) => d.key));
    // Зависимые умолчания реестра (`defaultWhen`, auto-ads решение 172): человек
    // сменил оптимизацию на «Ценность (pLTV)» → окно атрибуции «Клик 7 дн.» с
    // подсказкой, если окно в этой сессии он не трогал (явное изменение поля, а
    // не сравнение с умолчанием формы). Загрузка шаблона ничего не меняет.
    const [autoNotes, setAutoNotes] = useState({});
    const touchedRef = useRef(new Set());
    const beforeRef = useRef(new Map());
    // Radix-радиогруппа на программную смену значения сама присылает `change`
    // (эхо): его не считаем действием человека
    const echoRef = useRef(new Map());
    const { fieldDescs } = props;
    useEffect(() => {
        const dependents = fieldDescs.filter((d) => d.defaultWhen && d.defaultWhen.length > 0);
        if (dependents.length === 0)
            return;
        const sub = form.watch((all, info) => {
            // `type: 'change'` — только ввод человека; setValue ниже события не даёт
            if (info.type !== 'change' || !info.name?.startsWith('values.'))
                return;
            const key = fieldKeyOf(info.name.slice('values.'.length));
            if (dependents.some((d) => d.key === key)) {
                const now = all.values?.[fieldName(key)];
                const echo = echoRef.current.get(key);
                echoRef.current.delete(key);
                if (echo !== undefined && String(now) === echo)
                    return;
                touchedRef.current.add(key);
                beforeRef.current.delete(key);
                setAutoNotes((n) => (key in n ? omitKey(n, key) : n));
                return;
            }
            const live = fromFormValues(fieldDescs, all.values);
            for (const c of dependencyChange(dependents, key, live, touchedRef.current, beforeRef.current)) {
                const d = dependents.find((x) => x.key === c.key);
                if (!d)
                    continue;
                if (c.kind === 'switched') {
                    if (!beforeRef.current.has(c.key))
                        beforeRef.current.set(c.key, live[c.key]);
                }
                else
                    beforeRef.current.delete(c.key);
                const ui = toUiValue(d, c.value);
                echoRef.current.set(c.key, String(ui));
                form.setValue(`values.${fieldName(c.key)}`, ui, { shouldDirty: true });
                const note = c.note;
                setAutoNotes((n) => (note === null ? omitKey(n, c.key) : { ...n, [c.key]: note }));
            }
        });
        return () => sub.unsubscribe();
    }, [form, fieldDescs]);
    const launchCount = props.launchFields.length + props.launchOptions.length;
    return (_jsxs(_Fragment, { children: [_jsx(RegistryFields, { descriptors: props.tplFields, prefix: "values", grouped: true, issues: commonIssues, contextValues: live, disabledKeys: everyGroupKeys, ...(props.v4
                    ? {
                        groupLabel: (g) => g === 'adset' ? 'Группа объявлений — общее для всех' : GROUP_LABEL_RU_V4(g),
                        insertAfter: {
                            group: 'targeting',
                            node: (_jsx(StructureCard, { descs: props.structureOptions, issues: commonIssues, live: live, children: _jsx(TemplateGroups, { groupable: props.groups.groupable, live: live, issues: [...props.issues, ...props.groups.savedIssues], feature: props.groups.feature, onRetryFeature: props.groups.onRetryFeature, canEdit: props.groups.canEdit, bidLaunchEditable: bidLaunchEditable, savedHasGroups: props.groups.savedHasGroups, max: props.groups.max, templateMin: props.groups.templateMin, kind: kind, onLookalikeMoved: props.groups.onLookalikeMoved, labelOf: props.groups.labelOf }) })),
                        },
                    }
                    : {}), renderExtra: (d) => (_jsxs(_Fragment, { children: [d.key === GENDERS_KEY ? _jsx(GenderAdvantageNotice, { values: live, prefix: "values" }) : null, props.v4 ? (_jsx(CommonFieldGroupsNote, { desc: d, groups: formGroups, groupable: props.groups.groupable, movedLookalike: props.groups.movedLookalike })) : null, autoNotes[d.key] ? (_jsx(InlineNotice, { variant: "info", bordered: true, icon: Lightbulb, as: "div", "data-testid": `auto-default-note-${d.key}`, children: autoNotes[d.key] })) : null, _jsx(FieldFlags, { desc: d })] })) }), props.startOptions.length > 0 ? (_jsx(SectionCard, { title: "\u0421\u043E\u0437\u0434\u0430\u043D\u0438\u0435 \u0438 \u0441\u0442\u0430\u0440\u0442", hint: _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0412\u0441\u0451 \u0432\u0440\u0435\u043C\u044F \u043A\u043E\u043D\u0441\u0442\u0440\u0443\u043A\u0442\u043E\u0440\u0430 \u2014 \u0432\u0440\u0435\u043C\u044F \u041B\u043E\u0441-\u0410\u043D\u0434\u0436\u0435\u043B\u0435\u0441\u0430." }), children: _jsx(RegistryFields, { descriptors: props.startOptions, prefix: "options", issues: commonIssues, contextValues: live }) })) : null, props.tplOptions.length > 0 ? (_jsx(SectionCard, { title: props.v4 ? 'Правила и сторожа' : 'Копии и правила', children: _jsx(RegistryFields, { descriptors: props.tplOptions, prefix: "options", issues: commonIssues, contextValues: live }) })) : null, launchCount > 0 ? (_jsx(SectionCard, { title: "\u0417\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E \u043F\u0440\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u0435", hint: _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u042D\u0442\u0438 \u043F\u043E\u043B\u044F \u0437\u0430\u0434\u0430\u044E\u0442 \u043D\u0430 \u044D\u043A\u0440\u0430\u043D\u0435 \u0437\u0430\u043F\u0443\u0441\u043A\u0430; \u0437\u0434\u0435\u0441\u044C \u2014 \u0447\u0442\u043E \u043F\u043E\u0434\u0441\u0442\u0430\u0432\u0438\u0442\u0441\u044F \u0441\u0440\u0430\u0437\u0443. \u041F\u0443\u0441\u0442\u043E \u2014 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435 \u0432\u0432\u043E\u0434\u044F\u0442 \u043F\u0440\u0438 \u043A\u0430\u0436\u0434\u043E\u043C \u0437\u0430\u043F\u0443\u0441\u043A\u0435." }), children: _jsxs("div", { className: "flex flex-col gap-4", "data-testid": "launch-defaults", children: [_jsx(RegistryFields, { descriptors: props.launchFields, prefix: "values", issues: commonIssues, contextValues: live, renderExtra: (d) => _jsx(FieldFlags, { desc: d }) }), _jsx(RegistryFields, { descriptors: props.launchOptions, prefix: "options", issues: commonIssues, contextValues: live })] }) })) : null] }));
}
/** Подпись группы реестра на v4-экране (кроме «Группы объявлений», у неё своя). */
function GROUP_LABEL_RU_V4(g) {
    return GROUP_LABEL_RU[g] ?? g;
}
/**
 * Карточка «Структура» шаблона без групп (v4 §11.1): раскладка и копии (по
 * умолчанию при запуске), шаг бюджета копии и уникальные центы, лесенка ставок.
 * Лесенка — только при «Предельной ставке» / «Цели по цене» и с ОДНОЙ границей
 * по направлению шага («вверх» — «Не выше», «вниз» — «Не ниже»); вторая
 * граница не показывается и не стирается (её игнорирует сервер).
 */
function StructureCard({ descs, issues, live, children, }) {
    if (descs.length === 0)
        return null;
    const by = new Map(descs.map((d) => [d.key, d]));
    const pick = (...keys) => keys.map((k) => by.get(k)).filter((d) => d != null);
    const defaults = pick('opt.layout', 'opt.copies');
    const copies = pick('opt.budgetStepPct', 'opt.uniqueCents');
    return (_jsx(SectionCard, { title: "\u0421\u0442\u0440\u0443\u043A\u0442\u0443\u0440\u0430", children: _jsxs("div", { className: "flex flex-col gap-4", "data-testid": "structure-card", children: [defaults.length > 0 ? (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E \u043F\u0440\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u0435" }), _jsx(RegistryFields, { descriptors: defaults, prefix: "options", issues: issues, contextValues: live })] })) : null, copies.length > 0 ? (_jsx(RegistryFields, { descriptors: copies, prefix: "options", issues: issues, contextValues: live })) : null, _jsx(LadderFields, { descs: pick(...LADDER_KEYS), prefix: "options", live: live, issues: issues }), children] }) }));
}
/**
 * Схема групп формы шаблона (v4 §3.5, §4.3): 2–10 групп и названия латиницей
 * без повторов — те же правила, что у контракта и auto-ads; при выключенных
 * группах (только чтение) не проверяется — уходит только «убрать».
 */
function templateGroupsSchema(rules, on) {
    const group = z.object({
        key: z.string().optional(),
        label: z.string(),
        values: z.record(z.string(), z.unknown()),
    });
    return z.array(group).superRefine((gs, ctx) => {
        if (!on || gs.length === 0)
            return;
        if (gs.length < rules.templateMin) {
            ctx.addIssue({
                code: 'custom',
                message: 'Одна группа — то же самое, что без групп. Добавьте вторую или уберите группы',
            });
        }
        if (gs.length > rules.max) {
            ctx.addIssue({ code: 'custom', message: `Не больше ${rules.max} групп в шаблоне` });
        }
        gs.forEach((g, i) => {
            const others = gs
                .map((o, j) => ({ label: o.label, index: j }))
                .filter((o) => o.index < i && o.label !== '');
            const msg = groupLabelError(g.label, others);
            if (msg)
                ctx.addIssue({ code: 'custom', path: [i, 'label'], message: msg });
        });
    });
}
/** Копия словаря подсказок без одного поля. */
function omitKey(n, key) {
    const { [key]: _drop, ...rest } = n;
    return rest;
}
/**
 * Флажок шаблона под полем: «можно менять при запуске» (только у полей
 * шаблона — поле запуска меняется всегда).
 */
function FieldFlags({ desc }) {
    if (desc.mode !== 'template')
        return null;
    return (_jsx("div", { className: "flex flex-wrap gap-4 pl-1", "data-testid": `flags-${desc.key}`, children: _jsx(ZodField, { name: `flags.${le(desc.key)}` }) }));
}
/**
 * Замечания шаблона по родам: значения, не прошедшие проверку (`invalid`), —
 * ошибка «исправьте и сохраните»; неизвестные / устаревшие поля —
 * предупреждение «шаблон разошёлся с реестром»; советы — нейтральный блок
 * «Рекомендации».
 */
export function TemplateIssuesNotice({ issues }) {
    const { drift, invalid, advice } = splitTemplateIssues(issues);
    return (_jsxs(_Fragment, { children: [invalid.length > 0 ? (_jsxs(InlineNotice, { variant: "danger", bordered: true, icon: CircleAlert, as: "div", "data-testid": "template-invalid", children: ["\u0417\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u0448\u0430\u0431\u043B\u043E\u043D\u0430 \u043D\u0435 \u043F\u0440\u043E\u0448\u043B\u0438 \u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0443 \u2014 \u0438\u0441\u043F\u0440\u0430\u0432\u044C\u0442\u0435 \u043E\u0442\u043C\u0435\u0447\u0435\u043D\u043D\u044B\u0435 \u043F\u043E\u043B\u044F \u0438 \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u0435:", ' ', invalid.map((i) => i.message).join('; ')] })) : null, drift.length > 0 ? (_jsxs(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "template-drift", children: ["\u0412 \u0448\u0430\u0431\u043B\u043E\u043D\u0435 \u0435\u0441\u0442\u044C \u043F\u043E\u043B\u044F, \u043A\u043E\u0442\u043E\u0440\u044B\u0445 \u0440\u0435\u0435\u0441\u0442\u0440 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u0437\u043D\u0430\u0435\u0442 \u0438\u043B\u0438 \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0443\u0441\u0442\u0430\u0440\u0435\u043B\u0438:", ' ', drift.map((i) => i.message).join('; ')] })) : null, advice.length > 0 ? (_jsxs(InlineNotice, { variant: "info", bordered: true, icon: Lightbulb, as: "div", "data-testid": "template-advice", children: [_jsx("span", { className: "font-medium", children: "\u0420\u0435\u043A\u043E\u043C\u0435\u043D\u0434\u0430\u0446\u0438\u0438:" }), ' ', advice.map((i) => i.message).join('; ')] })) : null] }));
}
const NAME_PATTERNS = [
    { key: 'campaign', label: 'Кампания' },
    { key: 'adset', label: 'Группа объявлений' },
    { key: 'ad', label: 'Объявление' },
];
/** Пауза ввода перед запросом примера имён. */
const NAMING_SAMPLE_DEBOUNCE_MS = 300;
/**
 * Запрос примера имён из того, что сейчас в форме: только непустые шаблоны;
 * значение, которое auto-ads всё равно отвергнет (вид шаблона не выбран,
 * длина сверх предела), — без запроса (null).
 */
export function namingSampleRequest(naming, kind) {
    const levels = Object.fromEntries(NAME_PATTERNS.map((p) => [p.key, naming[p.key] ?? '']).filter(([, v]) => v !== ''));
    if (Object.keys(levels).length === 0)
        return null;
    const parsed = AdsNamingSampleRequest.safeParse({
        prefix: naming.prefix ?? '',
        kind,
        ...levels,
    });
    return parsed.success ? parsed.data : null;
}
/**
 * Пример имён шаблона — от auto-ads (`POST /registry/naming-sample`), по тем
 * же правилам, что у запуска; запрос — после паузы ввода. Пока идёт новый
 * запрос, виден прежний пример; если запрос невалиден или вернул ошибку —
 * старый пример не показывается (данные `undefined`).
 */
function useNamingSample(naming, kind) {
    // откладывается строка, а не объект: новый объект на каждом рендере
    // перезапускал бы таймер паузы бесконечно
    const key = useDebouncedValue(JSON.stringify(namingSampleRequest(naming, kind)), NAMING_SAMPLE_DEBOUNCE_MS);
    const req = useMemo(() => JSON.parse(key), [key]);
    const query = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: [...NAMING_SAMPLE_KEY, req],
        queryFn: () => getNamingSample(req),
        enabled: req != null,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
    // keepPreviousData отдаёт прежний ответ и при выключенном запросе, и пока
    // ошибка не осела: устаревший пример под новым значением вводит в заблуждение
    return {
        data: req == null || query.isError ? undefined : query.data,
        isError: query.isError,
        error: query.error,
    };
}
/**
 * «Названия»: префикс и три шаблона во всю ширину (длинный шаблон виден
 * целиком), справка по токенам — компактной таблицей «токен → смысл», под
 * каждым шаблоном — пример готового имени от auto-ads и его замечания
 * (неизвестный токен).
 */
function NamingFields({ tokens }) {
    const { form } = useZodFormContext();
    const naming = (form.watch('naming') ?? {});
    const kind = String(form.watch('kind') ?? '');
    const sample = useNamingSample(naming, kind);
    return (_jsxs("div", { className: "flex flex-col gap-4", "data-testid": "naming-fields", children: [tokens.length > 0 ? (_jsx("dl", { className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs sm:grid-cols-[auto_1fr_auto_1fr]", "data-testid": "naming-tokens", children: tokens.map((t) => (_jsxs("div", { className: "contents", children: [_jsx("dt", { children: _jsx("code", { className: "rounded-[var(--radius-1)] bg-[var(--color-surface-sunken)] px-1 font-mono", children: t.token }) }), _jsx("dd", { className: "text-[var(--color-text-secondary)]", children: t.label })] }, t.token))) })) : null, _jsx("div", { className: "sm:max-w-xs", children: _jsx(ZodField, { name: "naming.prefix" }) }), NAME_PATTERNS.map((p) => {
                const pattern = naming[p.key] ?? '';
                const name = pattern !== '' ? (sample.data?.names[p.key] ?? null) : null;
                const issues = pattern !== ''
                    ? (sample.data?.issues ?? []).filter((i) => i.fieldKey === `naming.${p.key}`)
                    : [];
                return (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(ZodField, { name: `naming.${p.key}` }), name != null ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)] [overflow-wrap:anywhere]", "data-testid": `naming-preview-${p.key}`, children: name === '' ? ('Пример: имя получится пустым') : (_jsxs(_Fragment, { children: ["\u041F\u0440\u0438\u043C\u0435\u0440: ", _jsx("span", { className: "font-mono", children: name })] })) })) : null, issues.map((i) => (_jsx("span", { className: "text-xs text-[var(--color-state-danger-fg)]", "data-testid": `naming-issue-${p.key}`, children: i.message }, `${i.code}-${i.message}`)))] }, p.key));
            }), sample.isError ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "naming-sample-error", children: ["\u041F\u0440\u0438\u043C\u0435\u0440 \u0438\u043C\u0435\u043D\u0438 \u0441\u0435\u0439\u0447\u0430\u0441 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D: ", describeAdsError(sample.error)] })) : null] }));
}
//# sourceMappingURL=template.js.map